// Avatar catalog — curated picker options the player can set on their profile.
//
// An avatar field is stored as a short string id. Rendering picks the right
// renderer for it:
//
//   1. "default" or missing            → initials in a name-colored circle
//   2. "suit:spade|heart|diamond|club" → giant suit glyph in a colored circle
//   3. "emoji:🔥|🐺|💎..."              → the raw emoji
//   4. "dice:<style>:<seed>"           → DiceBear SVG (fetched from CDN)
//
// Keeping the id string-based means we can mix old profiles (default, initials)
// with new presets freely, without a DB migration.

const DICEBEAR_BASE = "https://api.dicebear.com/9.x";

export function isPresetAvatar(id) {
  if (!id || id === "default") return false;
  return id.startsWith("suit:") || id.startsWith("emoji:") || id.startsWith("dice:");
}

export function dicebearUrl(style, seed) {
  const s = encodeURIComponent(seed || "quattro");
  // backgroundType=gradientLinear gives each avatar a punchy coloured ring
  // radius=50 = fully circular crop inside the SVG
  return `${DICEBEAR_BASE}/${style}/svg?seed=${s}&radius=50&backgroundType=gradientLinear`;
}

// ---------- Avatar catalog -----------------------------------------------

export const SUIT_AVATARS = [
  { id: "suit:spade",   glyph: "♠", color: "#B537F2", label: "Spades" },
  { id: "suit:heart",   glyph: "♥", color: "#FF3D6E", label: "Hearts" },
  { id: "suit:diamond", glyph: "♦", color: "#00C2FF", label: "Diamonds" },
  { id: "suit:club",    glyph: "♣", color: "#3DDC97", label: "Clubs" },
];

// 12 broadly recognizable emojis — intentionally gender-/culture-neutral.
export const EMOJI_AVATARS = [
  "🎯", "🔥", "👑", "💎", "🚀", "⚡",
  "🦊", "🐺", "🦁", "🐲", "🌟", "🎭",
].map((e) => ({ id: `emoji:${e}`, emoji: e }));

// DiceBear avatars — 12 curated seeds on the Adventurer style (cartoon faces).
// Seeds are stable so every user with the same pick gets the same art.
export const DICEBEAR_AVATARS = [
  { id: "dice:adventurer:Ace",    style: "adventurer", seed: "Ace" },
  { id: "dice:adventurer:Ruby",   style: "adventurer", seed: "Ruby" },
  { id: "dice:adventurer:Kai",    style: "adventurer", seed: "Kai" },
  { id: "dice:adventurer:Nova",   style: "adventurer", seed: "Nova" },
  { id: "dice:adventurer:Axel",   style: "adventurer", seed: "Axel" },
  { id: "dice:adventurer:Sage",   style: "adventurer", seed: "Sage" },
  { id: "dice:bottts:Rex",        style: "bottts",     seed: "Rex" },
  { id: "dice:bottts:Pixel",      style: "bottts",     seed: "Pixel" },
  { id: "dice:bottts:Glitch",     style: "bottts",     seed: "Glitch" },
  { id: "dice:pixel-art:Neon",    style: "pixel-art",  seed: "Neon" },
  { id: "dice:pixel-art:Shadow",  style: "pixel-art",  seed: "Shadow" },
  { id: "dice:pixel-art:Ember",   style: "pixel-art",  seed: "Ember" },
];

export const AVATAR_CATALOG = {
  suit: SUIT_AVATARS,
  emoji: EMOJI_AVATARS,
  dice: DICEBEAR_AVATARS,
};

// ---------- Parser / helpers for renderers -------------------------------

export function parseAvatar(id) {
  if (!id || id === "default") return { kind: "default" };
  if (id.startsWith("suit:")) {
    const kind = id.slice(5);
    const entry = SUIT_AVATARS.find((s) => s.id === id);
    return { kind: "suit", glyph: entry?.glyph || "♠", color: entry?.color || "#3DDC97", suit: kind };
  }
  if (id.startsWith("emoji:")) {
    return { kind: "emoji", emoji: id.slice(6) };
  }
  if (id.startsWith("dice:")) {
    const [, style, ...rest] = id.split(":");
    return { kind: "dice", style, seed: rest.join(":"), url: dicebearUrl(style, rest.join(":")) };
  }
  return { kind: "default" };
}
